import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'poster' | 'horizontal' | 'ranking';
  rank?: number;
}

export default function MovieCard({ movie, variant = 'poster', rank }: MovieCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="flex gap-3 p-2 rounded-lg hover:bg-background-800/40 transition-all group cursor-pointer"
      >
        <div className="w-28 h-40 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            title={`${movie.title} - 被窝影院高清在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-between py-1 min-w-0">
          <div>
            <h3 className="text-sm font-label font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
              {movie.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{movie.enTitle}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500/15 text-accent-400">
                {movie.rating}
              </span>
              <span className="text-xs text-foreground-500">{movie.year}</span>
              <span className="text-xs text-foreground-500">{movie.duration}</span>
            </div>
          </div>
          <p className="text-xs text-foreground-500 line-clamp-2">{movie.description}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'ranking' && rank) {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-800/40 transition-all group cursor-pointer"
      >
        <span
          className={`w-7 h-7 flex items-center justify-center text-sm font-label font-bold rounded-full flex-shrink-0 ${
            rank <= 3
              ? 'bg-primary-500 text-background-950'
              : 'bg-background-700 text-foreground-400'
          }`}
        >
          {rank}
        </span>
        <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            title={`${movie.title} - 被窝影院热门排行`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-label font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
            {movie.title}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-accent-400">{movie.rating}</span>
            <span className="text-xs text-foreground-500">{movie.genre.slice(0, 2).join('/')}</span>
          </div>
        </div>
        {movie.isHot && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-accent-500/15 text-accent-400 whitespace-nowrap flex-shrink-0">
            热播
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="block group cursor-pointer"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-800">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 被窝影院高清免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
          <p className="text-xs text-foreground-200 line-clamp-3">{movie.description}</p>
        </div>
        {movie.isNew && (
          <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full bg-accent-500 text-background-950 font-label font-medium">
            新
          </span>
        )}
        {movie.isHot && !movie.isNew && (
          <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full bg-primary-500 text-background-950 font-label font-medium">
            热播
          </span>
        )}
        <div className="absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded bg-black/60 text-accent-400 font-label font-semibold">
          {movie.rating}
        </div>
      </div>
      <div className="mt-2 px-0.5">
        <h3 className="text-sm font-label font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{movie.year} · {movie.genre[0]}</p>
      </div>
    </Link>
  );
}